import { UserRole } from "@/shared/types/user-role";
import { get, put, del } from "@/shared/utils/fetchApi";
import { sameOriginProxyUrl } from "@/shared/utils/proxyUrl";

/**
 * Interface que representa um usuário com seus produtos
 */
export interface User {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
  status: string;
  role?: UserRole;
  organization_id?: string;
  products?: Workspace[];
}

/**
 * Interface que representa um produto
 */
export interface Workspace {
  id: string;
  name: string;
  description?: string;
  logo_url?: string;
}

/**
 * Normaliza os produtos embutidos na resposta (ProductDto snake_case do api-v2)
 * para o shape Workspace usado na UI. Tolerante a ausência (legacy não embute).
 */
function mapWorkspaces(products: User["products"]): Workspace[] {
  return (products ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    description: p.description,
    logo_url: sameOriginProxyUrl(p.logo_url),
  }));
}

/**
 * Classe de serviço para encapsular todas as operações relacionadas aos usuários
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class UserService {
  /**
   * Pesquisa usuários ativos da organização atual por nome completo ou e-mail.
   * Server-side; usado pelo botão de pesquisa nas listas de usuários.
   */
  static async searchCurrentOrganizationUsers(
    query: string,
    options: { page?: number; limit?: number } = {},
  ): Promise<{ users: User[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams({ query });
    if (options.page) params.set("page", String(options.page));
    if (options.limit) params.set("limit", String(options.limit));

    // `?include=products` traz os workspaces embutidos, e a resposta é
    // `{users, pagination}` — sem `data` em volta.
    params.set("include", "products");
    const result = await get<{
      users: User[];
      pagination: { page: number; limit: number; total: number };
    }>(`/users/organization/current/search?${params.toString()}`);

    const activeUsers = (result.users || [])
      .filter((u) => u.status === "active")
      .map((u) => ({
        id: u.id,
        full_name: u.full_name || u.email?.split("@")[0] || "Usuário",
        email: u.email,
        avatar_url: sameOriginProxyUrl(u.avatar_url),
        status: u.status,
        role: u.role || UserRole.USER,
        organization_id: u.organization_id,
        products: mapWorkspaces(u.products),
      }));

    return {
      users: activeUsers,
      total: result.pagination?.total ?? activeUsers.length,
      page: result.pagination?.page ?? 1,
      limit: result.pagination?.limit ?? activeUsers.length,
    };
  }

  /**
   * Pesquisa usuários atribuídos a um workspace por nome completo ou e-mail.
   * Server-side; usado pelo botão de pesquisa na lista de usuários do workspace.
   */
  static async searchWorkspaceUsers(
    productId: string,
    query: string,
    options: { page?: number; limit?: number } = {},
  ): Promise<{ users: User[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams({ query });
    if (options.page) params.set("page", String(options.page));
    if (options.limit) params.set("limit", String(options.limit));

    // `/products/:id/users/search` returns `{users, pagination}` (no `data`
    // wrapper). Legacy: `/products/users/:id/search` returns `{data, pagination}`.
    const result = await get<{
      users: User[];
      pagination: { page: number; limit: number; total: number };
    }>(`/products/${productId}/users/search?${params.toString()}`);

    const activeUsers = (result.users || [])
      .filter((u) => u.status === "active")
      .map((u) => ({
        id: u.id,
        full_name: u.full_name || u.email?.split("@")[0] || "Usuário",
        email: u.email,
        avatar_url: sameOriginProxyUrl(u.avatar_url),
        status: u.status,
        role: u.role || UserRole.USER,
        organization_id: u.organization_id,
        products: [],
      }));

    return {
      users: activeUsers,
      total: result.pagination?.total ?? activeUsers.length,
      page: result.pagination?.page ?? 1,
      limit: result.pagination?.limit ?? activeUsers.length,
    };
  }

  /**
   * Obtém todos os usuários da organização atual usando o token JWT
   * @returns Lista de usuários da organização atual
   */
  static async getCurrentOrganizationUsers(): Promise<User[]> {
    try {
      // `/users/organization/current?include=products` devolve flat User[] com os
      // workspaces de cada usuário embutidos (evita o N+1 de getUserProducts por linha).
      // Legacy: `/profiles/organization/current/users` retorna `{data: User[]}` sem produtos.
      const rows: User[] = await get<User[]>(
        `/users/organization/current?include=products`,
      );

      const activeUsers = (rows ?? [])
        .filter((u) => u.status === "active")
        .map((u) => ({
          id: u.id,
          full_name: u.full_name || u.email?.split("@")[0] || "Usuário",
          email: u.email,
          avatar_url: sameOriginProxyUrl(u.avatar_url),
          status: u.status,
          role: u.role || UserRole.USER,
          organization_id: u.organization_id,
          products: mapWorkspaces(u.products),
        }));

      return activeUsers;
    } catch (error) {
      console.error("Erro ao obter usuários da organização atual:", error);
      throw error;
    }
  }

  /**
   * Obtém os usuários vinculados a um workspace direto da membership (product_users),
   * MESMA fonte da contagem exibida no card de workspaces. Diferente de
   * `getCurrentOrganizationUsers`, NÃO esconde superadmins nem filtra por status —
   * um membro é um membro. Usado como fonte da lista default da aba de usuários do
   * workspace (só no path v2; o legacy não embute perfil nesse endpoint).
   */
  static async getWorkspaceMembers(productId: string): Promise<User[]> {
    // `/products/:id/users` devolve o perfil completo de cada membro
    // (id=profile, status, role de plataforma). Legacy não embute perfil aqui.
    const rows = await get<
      Array<{
        id: string;
        full_name: string | null;
        email: string;
        avatar_url: string | null;
        status?: string | null;
        role?: UserRole;
        organization_id?: string;
      }>
    >(`/products/${productId}/users`);

    return (rows || []).map((u) => ({
      id: u.id,
      full_name: u.full_name || u.email?.split("@")[0] || "Usuário",
      email: u.email,
      avatar_url: sameOriginProxyUrl(u.avatar_url),
      status: u.status ?? "active",
      role: u.role || UserRole.USER,
      organization_id: u.organization_id,
      products: [],
    }));
  }

  /**
   * Obtém os produtos associados a um usuário
   * @param userId ID do usuário
   * @returns Lista de produtos do usuário
   */
  static async getUserProducts(userId: string): Promise<Workspace[]> {
    try {
      // v2 reestrutura URL: /users/:userId/products (nested REST).
      return (await get<Workspace[]>(`/users/${userId}/products`)) || [];
    } catch (error) {
      console.error(`Erro ao obter produtos do usuário ${userId}:`, error);
      return [];
    }
  }

  /**
   * Remove o usuário da organização ATIVA (AB#84509): cai o vínculo com ela e os
   * workspaces dela. A conta e as demais organizações continuam intactas — a
   * exclusão de conta existe num lugar só, o próprio perfil da pessoa.
   *
   * A rota mantém o sufixo `/admin` por compatibilidade; foi o comportamento dela
   * que mudou, não o endereço.
   */
  static async removeUserFromOrganization(userId: string): Promise<void> {
    // v2: mesma rota, sem o prefixo /api do client legacy

    await del<Record<string, unknown>>(`/users/${userId}/admin`);
    return;
  }

  /**
   * Desativa um usuário
   * @param userId ID do usuário a ser desativado
   * @returns Resposta da API
   */
  static async deactivateUser(
    userId: string,
  ): Promise<Record<string, unknown>> {
    try {
      // v2: `PUT /users/:id {status:'inactive'}` (admin). Legacy: `PUT /profiles/:id`.

      return await put<Record<string, unknown>>(`/users/${userId}`, {
        status: "inactive",
      });
    } catch (error) {
      console.error(`Erro ao desativar usuário ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Atualiza os produtos de um usuário
   * @param userId ID do usuário
   * @param productIds IDs dos produtos a serem associados ao usuário
   * @returns Resposta da API
   */
  static async updateUserProducts(
    userId: string,
    productIds: string[],
  ): Promise<Record<string, unknown>> {
    try {
      // v2 reestrutura: PUT /users/:userId/products (legacy era POST /products/users/...).
      return await put<Record<string, unknown>>(`/users/${userId}/products`, {
        product_ids: productIds,
      });
    } catch (error) {
      console.error(`Erro ao atualizar produtos do usuário ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Auto-exclusão da própria conta: `DELETE /users/me` — o usuário vem do token.
   * @param validation Senha (Email/Password) ou a palavra "DELETE" (OAuth)
   * @param requiresPassword true envia `password`; false envia `confirmation`
   * @returns Resultado da operação
   */
  static async deleteSelf(
    validation: string,
    requiresPassword: boolean = true,
  ): Promise<{
    success: boolean;
    message?: string;
    data?: {
      deleted_profile: boolean;
      deleted_auth: boolean;
      deleted_settings: boolean;
      deleted_product_users: boolean;
    };
  }> {
    try {
      // Resposta é `{success:true}`, sem o breakdown `deleted_*` (opcional no tipo).
      const body = requiresPassword
        ? { password: validation }
        : { confirmation: validation };
      const result = await del<Record<string, unknown>>(`/users/me`, {
        body: body,
      });
      return result as {
        success: boolean;
        message?: string;
        data?: {
          deleted_profile: boolean;
          deleted_auth: boolean;
          deleted_settings: boolean;
          deleted_product_users: boolean;
        };
      };
    } catch (error: unknown) {
      console.error("Erro ao deletar a própria conta:", error);
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Erro ao deletar usuário",
      };
    }
  }
}

export default UserService;
